import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        return api(originalRequest);
      } catch (refreshError) {
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getDashboardAll = () => api.get('/dashboard/all');

// Meetings
export const getMeetings = () => api.get('/meetings');
export const getMeeting = (id) => api.get(`/meetings/${id}`);
export const createMeeting = (data) => api.post('/meetings', data);
export const updateMeetingStatus = (id, dealStatus) => api.patch(`/meetings/${id}/status`, { deal_status: dealStatus });

// Deal Stats
export const getDealStats = () => api.get('/deals/stats');

// Tasks
export const getTasks = (status) => api.get('/tasks', { params: status ? { status } : {} });
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.patch(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Follow-ups
export const getFollowUps = (meetingId) => api.get('/follow-ups', { params: meetingId ? { meeting_id: meetingId } : {} });
export const getFollowUp = (id) => api.get(`/follow-ups/${id}`);
export const generateFollowUp = (meetingId) => api.post('/follow-ups/generate', { meeting_id: meetingId });
export const updateFollowUp = (id, data) => api.patch(`/follow-ups/${id}`, data);
export const sendFollowUp = (id, recipientEmail) => api.post(`/follow-ups/${id}/send`, { recipient_email: recipientEmail });

// Notifications
export const getNotifications = (unreadOnly) => api.get('/notifications', { params: unreadOnly ? { unread_only: true } : {} });
export const markNotificationRead = (id) => api.patch(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/mark-all-read');

// Voice Transcription
export const transcribeAudio = (audioBlob) => {
  const formData = new FormData();
  // Use correct file extension based on actual blob type
  const ext = audioBlob.type?.includes('mp4') ? 'mp4' : audioBlob.type?.includes('mp3') ? 'mp3' : audioBlob.type?.includes('wav') ? 'wav' : 'webm';
  formData.append('audio', audioBlob, `recording.${ext}`);
  return api.post('/voice/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const extractMeetingFromTranscription = (transcription) => 
  api.post('/voice/extract-meeting', { transcription });

export const transcribeAndExtract = (audioBlob) => {
  const formData = new FormData();
  // Use correct file extension based on actual blob type
  const ext = audioBlob.type?.includes('mp4') ? 'mp4' : audioBlob.type?.includes('mp3') ? 'mp3' : audioBlob.type?.includes('wav') ? 'wav' : 'webm';
  formData.append('audio', audioBlob, `recording.${ext}`);
  return api.post('/voice/transcribe-and-extract', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000 // 120 second timeout for transcription + extraction
  });
};

// HubSpot Integration
export const getHubSpotStatus = () => api.get('/hubspot/status');
export const syncMeetingToHubSpot = (meetingId) => api.post(`/hubspot/sync-meeting/${meetingId}`);
export const updateHubSpotDealStatus = (meetingId, status) => 
  api.patch(`/hubspot/deal/${meetingId}/status`, { status });
export const deleteHubSpotDeal = (meetingId) => api.delete(`/hubspot/deal/${meetingId}`);

// Calendar Integration
export const getMeetingCalendarUrl = (meetingId) => 
  `${process.env.REACT_APP_BACKEND_URL}/api/calendar/meeting/${meetingId}.ics`;
export const getTaskCalendarUrl = (taskId) => 
  `${process.env.REACT_APP_BACKEND_URL}/api/calendar/task/${taskId}.ics`;
export const getAllCalendarUrl = () => 
  `${process.env.REACT_APP_BACKEND_URL}/api/calendar/export-all.ics`;

// Google Calendar URL generator
export const getGoogleCalendarUrl = (title, startDate, endDate, description, location) => {
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().replace(/-|:|\.\d+/g, '').slice(0, 15) + 'Z';
  };
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
    details: description || '',
    location: location || ''
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export default api;
